import React, { useState, useEffect } from "react";
import axios from "axios";
import { branding } from "../../config";

export function EventsCalendar() {
  const [events, setEvents] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDayEvents, setSelectedDayEvents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const res = await axios.get("/api/public/events");
        setEvents(res.data.content || []);
      } catch (err) {
        console.error("Error loading events:", err);
      }
    };
    loadEvents();
  }, []);

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month, 1).getDay();
  };

  const changeMonth = (delta) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + delta);
    setCurrentDate(newDate);
  };

  const getEventsForDate = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dayEvents = events.filter(event => event.date === dateStr);
    
    // Sort by first itinerary time if available
    return dayEvents.sort((a, b) => {
      const aTime = Array.isArray(a.time) && a.time.length > 0 && a.time[0].time ? a.time[0].time : '23:59';
      const bTime = Array.isArray(b.time) && b.time.length > 0 && b.time[0].time ? b.time[0].time : '23:59';
      return aTime.localeCompare(bTime);
    });
  };

  const isToday = (day) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    );
  };

  const daysInMonth = getDaysInMonth(currentDate);
  const firstDay = getFirstDayOfMonth(currentDate);
  const days = [];

  // Add empty cells for days before the first day of the month
  for (let i = 0; i < firstDay; i++) {
    days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
  }

  // Add cells for each day of the month
  for (let day = 1; day <= daysInMonth; day++) {
    const dayEvents = getEventsForDate(day);
    const hasEvents = dayEvents.length > 0;
    const today = isToday(day);

    days.push(
      <div
        key={day}
        className={`calendar-day ${hasEvents ? 'has-events' : ''} ${today ? 'today' : ''}`}
        onClick={() => hasEvents && setSelectedDayEvents(dayEvents)}
        style={{
          cursor: hasEvents ? 'pointer' : 'default'
        }}
      >
        <div className="day-number">{day}</div>
        {hasEvents && (
          <div className="event-indicators">
            {dayEvents.slice(0, 2).map((event, i) => (
              <div
                key={i}
                className="event-dot"
                title={event.name}
                style={{ backgroundColor: branding.primaryColor }}
              >
                <span className="event-name">{event.name}</span>
              </div>
            ))}
            {dayEvents.length > 2 && (
              <small className="text-muted">+{dayEvents.length - 2} more</small>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="events-calendar">
      <style>{`
        .events-calendar {
          max-width: 1000px;
          margin: 0 auto;
        }
        
        .events-scroll-container::-webkit-scrollbar {
          height: 8px;
        }
        .events-scroll-container::-webkit-scrollbar-track {
          background: #f0f0f0;
          border-radius: 4px;
        }
        .events-scroll-container::-webkit-scrollbar-thumb {
          background: ${branding.primaryColor};
          border-radius: 4px;
        }
        .events-scroll-container::-webkit-scrollbar-thumb:hover {
          background: ${branding.primaryColor}dd;
        }
        
        .calendar-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          padding: 0 1rem;
        }
        
        .calendar-header h3 {
          margin: 0;
          font-weight: 600;
          color: ${branding.primaryColor};
        }
        
        .calendar-nav button {
          background: none;
          border: 1px solid ${branding.primaryColor};
          color: ${branding.primaryColor};
          padding: 0.5rem 1rem;
          margin: 0 0.5rem;
          border-radius: 5px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.2s;
        }
        
        .calendar-nav button:hover {
          background: ${branding.primaryColor};
          color: white;
        }
        
        .calendar-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 1px;
          background: #dee2e6;
          border: 1px solid #dee2e6;
        }
        
        .calendar-weekday {
          background: ${branding.primaryColor};
          color: white;
          padding: 0.75rem;
          text-align: center;
          font-weight: 600;
          font-size: 0.9rem;
        }
        
        .calendar-day {
          background: white;
          min-height: 100px;
          padding: 0.5rem;
          position: relative;
          overflow: hidden;
        }
        
        .calendar-day.empty {
          background: #f8f9fa;
        }
        
        .calendar-day.today {
          background: #e3f2fd;
          border: 2px solid ${branding.primaryColor};
        }
        
        .calendar-day.has-events:hover {
          background: #f0f0f0;
        }
        
        .day-number {
          font-weight: 600;
          color: #495057;
          margin-bottom: 0.5rem;
        }
        
        .calendar-day.today .day-number {
          color: ${branding.primaryColor};
          font-weight: 700;
        }
        
        .event-indicators {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          overflow: hidden;
        }
        
        .event-dot {
          padding: 0.25rem 0.5rem;
          border-radius: 3px;
          font-size: 0.75rem;
          color: white;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: 100%;
        }
        
        .event-name {
          color: white;
          font-weight: 500;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          display: block;
        }
        
        @media (max-width: 768px) {
          .calendar-day {
            min-height: 80px;
            font-size: 0.85rem;
          }
          
          .event-name {
            display: none;
          }
          
          .event-dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            padding: 0;
            min-height: 8px;
          }
        }
      `}</style>

      <div className="calendar-header">
        <div className="calendar-nav">
          <button onClick={() => changeMonth(-1)}>
            <i className="fa-solid fa-chevron-left"></i> Previous
          </button>
          <button onClick={() => changeMonth(1)}>
            Next <i className="fa-solid fa-chevron-right"></i>
          </button>
        </div>
        <h3>{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</h3>
      </div>

      <div className="calendar-grid">
        <div className="calendar-weekday">Sun</div>
        <div className="calendar-weekday">Mon</div>
        <div className="calendar-weekday">Tue</div>
        <div className="calendar-weekday">Wed</div>
        <div className="calendar-weekday">Thu</div>
        <div className="calendar-weekday">Fri</div>
        <div className="calendar-weekday">Sat</div>
        {days}
      </div>

      {selectedDayEvents.length > 0 && (
        <div className="mt-4">
          <h5 style={{ color: branding.primaryColor, fontWeight: 'bold' }} className="mb-3">
            Events on {monthNames[currentDate.getMonth()]} {selectedDayEvents[0].date.split('-')[2]}, {currentDate.getFullYear()}
          </h5>
          <div className="events-scroll-container" style={{
            display: 'flex',
            overflowX: 'auto',
            gap: '1rem',
            paddingBottom: '1rem',
            // justifyContent: 'center',
            scrollbarWidth: 'thin',
            scrollbarColor: `${branding.primaryColor} #f0f0f0`
          }}>
            {selectedDayEvents.map((event, idx) => (
              <div key={idx} style={{ minWidth: '320px', maxWidth: '320px' }}>
                <div className="card h-100 shadow-sm" style={{ border: 'none' }}>
                  <div className="card-body">
                    <h6 className="card-title fw-bold" style={{ color: branding.primaryColor }}>
                      {event.name}
                    </h6>
                    {event.description && (
                      <p className="card-text text-muted small" style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {event.description}
                      </p>
                    )}
                    {event.location && (
                      <p className="card-text mb-1 small">
                        <i className="fa-solid fa-location-dot me-2" style={{ color: branding.primaryColor }}></i>
                        {event.location}
                      </p>
                    )}
                    {event.time && event.time.length > 0 && event.time[0].time && (
                      <p className="card-text mb-2 small">
                        <i className="fa-solid fa-clock me-2" style={{ color: branding.primaryColor }}></i>
                        {new Date(`2000-01-01T${event.time[0].time}`).toLocaleTimeString(undefined, {
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        })}
                      </p>
                    )}
                    <button 
                      className="btn btn-sm btn-outline-primary"
                      style={{ 
                        borderColor: branding.primaryColor, 
                        color: branding.primaryColor 
                      }}
                      onClick={() => {
                        setSelectedEvent(event);
                        setShowModal(true);
                      }}
                      onMouseOver={(e) => {
                        e.target.style.backgroundColor = branding.primaryColor;
                        e.target.style.color = 'white';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = branding.primaryColor;
                      }}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && selectedEvent && (
        <>
          {/* Backdrop */}
          <div
            className="modal-backdrop fade show"
            onClick={() => {
              setShowModal(false);
              setSelectedEvent(null);
            }}
            style={{ zIndex: 1040 }}
          ></div>

          {/* Modal */}
          <div
            className="modal d-block fade show"
            tabIndex="-1"
            role="dialog"
            style={{ zIndex: 1050 }}
            onClick={() => {
              setShowModal(false);
              setSelectedEvent(null);
            }}
          >
            <div
              className="modal-dialog modal-dialog-centered"
              role="document"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content">
                <div className="modal-header" style={{ borderBottom: `2px solid ${branding.primaryColor}` }}>
                  <h5 className="modal-title ms-auto fw-bold" style={{ color: branding.primaryColor }}>
                    {selectedEvent.name}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => {
                      setShowModal(false);
                      setSelectedEvent(null);
                    }}
                  ></button>
                </div>
                <div className="modal-body">
                  {selectedEvent.description && (
                    <p className="mb-3" style={{ color: '#6c757d', lineHeight: '1.6' }}>
                      {selectedEvent.description}
                    </p>
                  )}
                  
                  <div className="mb-2">
                    <i className="fa-solid fa-calendar-days me-2" style={{ color: branding.primaryColor, width: '20px' }} />
                    <span className="fw-semibold" style={{ color: '#495057' }}>
                      {new Date(selectedEvent.date + "T00:00").toLocaleDateString(undefined, {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric"
                      })}
                    </span>
                  </div>

                  {selectedEvent.location && (
                    <div className="mb-1">
                      <i className="fa-solid fa-location-dot me-2" style={{ color: branding.primaryColor, width: '20px' }} />
                      <span className="fw-semibold" style={{ color: '#495057' }}>
                        {selectedEvent.location}
                      </span>
                    </div>
                  )}
                  
                  {selectedEvent.address && (
                    <div className="mb-3 ms-4 ps-2" style={{ color: '#6c757d', fontSize: '0.9rem' }}>
                      {selectedEvent.address}
                    </div>
                  )}

                  {/* Contacts */}
                  {Array.isArray(selectedEvent.contacts) && selectedEvent.contacts.length > 0 && (
                    <div className="mt-3">
                      <div className="mb-2">
                        <i className="fa-solid fa-address-book me-2" style={{ color: branding.primaryColor, width: '20px' }} />
                        <span className="fw-semibold" style={{ color: '#495057' }}>
                          Contact{selectedEvent.contacts.length > 1 ? 's' : ''}:
                        </span>
                      </div>
                      <ul className="mb-0 ms-4 ps-2" style={{ listStyleType: "none", paddingLeft: 0 }}>
                        {selectedEvent.contacts.map((contact, i) => (
                          <li key={i} className="mb-1" style={{ color: '#6c757d', fontSize: '0.9rem' }}>
                            {contact.name && <strong style={{ color: '#495057' }}>{contact.name}</strong>}
                            {contact.phone && <span> • {contact.phone}</span>}
                            {contact.email && (
                              <span>
                                {' • '}
                                <a 
                                  href={`mailto:${contact.email}`} 
                                  style={{ color: branding.primaryColor, textDecoration: 'none' }}
                                  onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                                  onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                                >
                                  {contact.email}
                                </a>
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Itinerary */}
                  {Array.isArray(selectedEvent.time) && selectedEvent.time.length > 0 && (
                    <div className="mt-3">
                      <div className="mb-2">
                        <i className="fa-solid fa-clipboard-list me-2" style={{ color: branding.primaryColor, width: '20px' }} />
                        <span className="fw-semibold" style={{ color: '#495057' }}>Itinerary:</span>
                      </div>
                      <ul className="mb-0 ms-4 ps-2" style={{ listStyleType: "none", paddingLeft: 0 }}>
                        {selectedEvent.time.map((t, i) => (
                          <li key={i} className="mb-1" style={{ color: '#6c757d', fontSize: '0.9rem' }}>
                            {t.time && (
                              <strong style={{ color: branding.primaryColor }}>
                                {new Date(`2000-01-01T${t.time}`).toLocaleTimeString(undefined, {
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  hour12: true
                                })}
                              </strong>
                            )}
                            {t.description && <span>{t.time ? ' — ' : ''}{t.description}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
